import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { MagnifyingGlass, CaretDown, Buildings, Check, X, Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import {
  LOCATIONS, DEPARTMENTS, SENIORITIES, CONTACT_TYPES, EMAIL_REQ, LINKEDIN_REQ,
  DATA_SOURCES, EMAIL_CONF, EXAMPLE_COMPANIES,
} from "@/lib/constants";
import SearchProgress from "@/components/SearchProgress";

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`px-3 h-9 rounded-md text-sm font-medium border transition-colors ${
      active ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
    }`}
  >
    {children}
  </button>
);

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <label className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500">{label}</label>
    {children}
  </div>
);

const Select = ({ value, onChange, options, testid }) => (
  <div className="relative">
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-11 pl-3 pr-9 rounded-md border border-zinc-200 bg-white text-sm appearance-none focus:ring-2 focus:ring-zinc-900/20 focus:border-zinc-900 outline-none transition-colors"
    >
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
    <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
  </div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const routerLoc = useLocation();
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [location, setLocation] = useState("Any");
  const [department, setDepartment] = useState("Marketing");
  const [seniority, setSeniority] = useState([]);
  const [contactType, setContactType] = useState([]);
  const [emailReq, setEmailReq] = useState("Any");
  const [linkedinReq, setLinkedinReq] = useState("Any");
  const [dataSource, setDataSource] = useState("All");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [titleKeywords, setTitleKeywords] = useState("");
  const [excludeTitles, setExcludeTitles] = useState("");
  const [minSeniority, setMinSeniority] = useState("");
  const [emailConf, setEmailConf] = useState("");

  const [resolving, setResolving] = useState(false);
  const [candidates, setCandidates] = useState(null);
  const [confirmed, setConfirmed] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const pf = routerLoc.state?.prefill;
    if (pf) {
      if (pf.company) setCompany(pf.company);
      if (pf.role) setRole(pf.role);
    }
  }, [routerLoc.state]);

  useEffect(() => {
    api.get("/settings/preferences").then((r) => {
      const p = r.data;
      if (p.default_location) setLocation(p.default_location);
      if (p.default_seniority?.length) setSeniority(p.default_seniority);
      if (p.default_departments?.length) setDepartment(p.default_departments[0]);
    }).catch(() => {});
  }, []);

  const toggle = (arr, setArr, v) =>
    setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const buildFilters = () => ({
    location, department, seniority, contact_type: contactType,
    email_requirement: emailReq, linkedin_requirement: linkedinReq,
    title_keywords: titleKeywords.split(",").map((s) => s.trim()).filter(Boolean),
    exclude_titles: excludeTitles.split(",").map((s) => s.trim()).filter(Boolean),
    min_seniority: minSeniority || null,
    email_confidence: emailConf ? Number(emailConf) : null,
    data_source: dataSource,
  });

  const resolveCompany = async () => {
    if (!company.trim() || !role.trim()) {
      toast.error("Enter a company and target role");
      return;
    }
    setResolving(true);
    try {
      const r = await api.post("/company/resolve", { query: company });
      const cands = r.data.candidates;
      if (cands.length === 1) {
        setConfirmed(cands[0]);
        runSearch(cands[0]);
      } else {
        setCandidates(cands);
      }
    } catch (e) {
      toast.error("Company not found. Try entering the company name or website.");
    } finally {
      setResolving(false);
    }
  };

  const runSearch = async (comp) => {
    setCandidates(null);
    setConfirmed(comp);
    setSearching(true);
    try {
      const r = await api.post("/people/search", {
        company: comp, target_role: role, filters: buildFilters(),
      });
      // keep progress on screen a touch for UX handled inside SearchProgress
      sessionStorage.setItem(`search_${r.data.search_id}`, JSON.stringify(r.data));
      setTimeout(() => navigate(`/app/results/${r.data.search_id}`), 400);
    } catch (e) {
      setSearching(false);
      toast.error("Data provider temporarily unavailable. Please retry.");
    }
  };

  if (searching) return <SearchProgress company={confirmed} role={role} />;

  return (
    <div className="max-w-4xl mx-auto px-8 py-14">
      <div className="mb-10 animate-fade-up">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-3">Hiring Decision Maker Finder</p>
        <h1 className="font-head text-4xl sm:text-5xl font-extrabold tracking-tighter mb-3">Find the people who can hire you.</h1>
        <p className="text-base text-zinc-600 max-w-2xl">
          Enter a company and target role. We'll identify the most relevant decision-makers and hiring contacts.
        </p>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl shadow-sm p-8 animate-fade-up">
        <div className="grid sm:grid-cols-2 gap-5 mb-6">
          <Field label="Company">
            <div className="relative">
              <Buildings size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                data-testid="company-input"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Enter company name"
                className="w-full h-11 pl-9 pr-3 rounded-md border border-zinc-200 bg-white text-sm focus:ring-2 focus:ring-zinc-900/20 focus:border-zinc-900 outline-none transition-colors"
              />
            </div>
          </Field>
          <Field label="Target Role">
            <input
              data-testid="role-input"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="e.g. Senior Performance Marketing Manager"
              className="w-full h-11 px-3 rounded-md border border-zinc-200 bg-white text-sm focus:ring-2 focus:ring-zinc-900/20 focus:border-zinc-900 outline-none transition-colors"
            />
          </Field>
        </div>

        <div className="grid sm:grid-cols-3 gap-5 mb-6">
          <Field label="Location"><Select testid="location-select" value={location} onChange={setLocation} options={LOCATIONS} /></Field>
          <Field label="Department"><Select testid="department-select" value={department} onChange={setDepartment} options={DEPARTMENTS} /></Field>
          <Field label="Data Source"><Select testid="datasource-select" value={dataSource} onChange={setDataSource} options={DATA_SOURCES} /></Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 mb-6">
          <Field label="Seniority">
            <div className="flex flex-wrap gap-2">
              {SENIORITIES.map((s) => (
                <Chip key={s} testid={`seniority-${s}`} active={seniority.includes(s)} onClick={() => toggle(seniority, setSeniority, s)}>{s}</Chip>
              ))}
            </div>
          </Field>
          <Field label="Contact Type">
            <div className="flex flex-wrap gap-2">
              {CONTACT_TYPES.map((s) => (
                <Chip key={s} testid={`contact-${s}`} active={contactType.includes(s)} onClick={() => toggle(contactType, setContactType, s)}>{s}</Chip>
              ))}
            </div>
          </Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-5 mb-2">
          <Field label="Email Requirement"><Select testid="email-req-select" value={emailReq} onChange={setEmailReq} options={EMAIL_REQ} /></Field>
          <Field label="LinkedIn Requirement"><Select testid="linkedin-req-select" value={linkedinReq} onChange={setLinkedinReq} options={LINKEDIN_REQ} /></Field>
        </div>

        {/* Advanced */}
        <button
          data-testid="advanced-toggle"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="mt-5 flex items-center gap-2 text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors"
        >
          <CaretDown size={14} className={`transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
          Advanced Filters
        </button>

        {showAdvanced && (
          <div className="mt-5 grid sm:grid-cols-2 gap-5 border-t border-zinc-100 pt-5 animate-fade-up">
            <Field label="Job Title Keywords">
              <input data-testid="keywords-input" value={titleKeywords} onChange={(e) => setTitleKeywords(e.target.value)}
                placeholder="performance marketing, paid media" className="w-full h-11 px-3 rounded-md border border-zinc-200 text-sm outline-none focus:border-zinc-900" />
            </Field>
            <Field label="Exclude Titles">
              <input data-testid="exclude-input" value={excludeTitles} onChange={(e) => setExcludeTitles(e.target.value)}
                placeholder="Intern, Assistant, Junior" className="w-full h-11 px-3 rounded-md border border-zinc-200 text-sm outline-none focus:border-zinc-900" />
            </Field>
            <Field label="Minimum Seniority"><Select testid="min-seniority-select" value={minSeniority || "Any"} onChange={(v) => setMinSeniority(v === "Any" ? "" : v)} options={["Any", ...SENIORITIES]} /></Field>
            <Field label="Email Confidence"><Select testid="email-confidence-select" value={emailConf || "Any"} onChange={(v) => setEmailConf(v === "Any" ? "" : v)} options={["Any", ...EMAIL_CONF.map(String)]} /></Field>
          </div>
        )}

        <button
          data-testid="find-contacts-button"
          onClick={resolveCompany}
          disabled={resolving}
          className="mt-8 w-full h-12 rounded-lg bg-zinc-900 text-white font-medium flex items-center justify-center gap-2 hover:bg-zinc-800 transition-colors active:scale-[0.99] disabled:opacity-60"
        >
          {resolving ? "Resolving company…" : <><MagnifyingGlass size={18} weight="bold" /> Find Hiring Contacts</>}
        </button>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <span className="text-xs text-zinc-400 mr-1">Try:</span>
        {EXAMPLE_COMPANIES.map((c) => (
          <button key={c} data-testid={`example-${c}`} onClick={() => setCompany(c)}
            className="text-xs px-2.5 h-7 rounded-md border border-zinc-200 bg-white hover:border-zinc-400 transition-colors">
            {c}
          </button>
        ))}
      </div>

      {/* Company selection modal */}
      {candidates && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl border border-zinc-200 shadow-2xl w-full max-w-lg p-6 animate-fade-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head text-xl font-bold tracking-tight">Confirm Company</h3>
              <button data-testid="close-candidates" onClick={() => setCandidates(null)} className="text-zinc-400 hover:text-zinc-900"><X size={20} /></button>
            </div>
            <div className="space-y-2">
              {candidates.map((c) => (
                <button key={c.domain} data-testid={`candidate-${c.domain}`} onClick={() => runSearch(c)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-zinc-200 hover:border-zinc-900 hover:bg-zinc-50 transition-colors text-left">
                  <img src={c.logo} alt="" className="w-10 h-10 rounded-md bg-zinc-100 object-contain" onError={(e) => (e.target.style.visibility = "hidden")} />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{c.name}</p>
                    <p className="text-xs text-zinc-500">{c.domain} · {c.industry}{c.size && c.size !== "Unknown" ? ` · ${c.size}` : ""} · {c.location}</p>
                    {c.description && <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{c.description}</p>}
                  </div>
                  <Check size={18} className="text-zinc-400" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
